import { notizSchluessel, type BlockNotiz } from "../../notizbloecke/blockModell";
import { zielSchluessel, zielVon } from "../../notizbloecke/kommentarModell";
import { SPRUNG_MARKE_KLASSE } from "../../notizbloecke/notizFeldDom";

/**
 * DIE SPRUNGMARKE EINER MITTEILUNG (User 01.11.2026).
 *
 * „Klick in der Mitteilung → Vorgang öffnet sich, das To-do klappt auf, es
 * scrollt genau zur Erwähnung und hebt sie temporär hervor." Die Mitteilung
 * trägt dafür `?todo=<id>&notiz=<quelle>:<id>` am Link (Backend:
 * `vorgangMitteilungsLink`). Dieselbe Marke setzen die Kontaktansicht und das
 * Kundentagebuch beim Klick auf ein To-do — dort nur `?todo=`.
 *
 * ══ WARUM `quelle:id` ══════════════════════════════════════════════════════
 * Notizen stehen in ZWEI Tabellen (`VorgangNote`, `TodoNote`) mit eigenen
 * Ids — `todo:5` und `vorgang:5` sind verschiedene Zeilen. Der Schlüssel ist
 * derselbe, den das Notizfeld als `data-nk` trägt (`notizSchluessel`). Eine
 * nackte Zahl (falls jemand den Link von Hand baut) wird toleriert.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Hier steht die REINE Ableitung „wohin springen" (node-prüfbar) und die
 * kleinen DOM-Handgriffe dazu. Wann gesprungen wird, entscheidet die Seite.
 */

/** Was die URL verlangt. */
export type Sprung = {
    todoId: number | null;
    /** `quelle:id` oder eine nackte Id; null = nur das To-do aufklappen. */
    notiz: string | null;
};

const positiv = (roh: string | null): number | null => {
    if (!roh) return null;
    const n = Number(roh);
    return Number.isInteger(n) && n > 0 ? n : null;
};

/** `?todo=` und `?notiz=` lesen; null = nichts zu tun. */
export function sprungAus(params: URLSearchParams): Sprung | null {
    const todoId = positiv(params.get("todo"));
    const notizRoh = params.get("notiz")?.trim() || null;
    const notiz = notizRoh && /^((vorgang|todo):)?\d+$/.test(notizRoh) ? notizRoh : null;
    if (todoId == null && notiz == null) return null;
    return { todoId, notiz };
}

/** Ein Ort im DOM, an dem die Notiz (oder ihr Umfeld) zu sehen ist. */
export type SprungKandidat = { attr: string; wert: string };

/** Wohin gesprungen wird. */
export type SprungZiel = {
    /** Dieses To-do klappt auf; null = keins (Notiz am Vorgang). */
    todoId: number | null;
    /**
     * Die Orte in der Reihenfolge, in der sie gesucht werden — der erste,
     * der im DOM steht, gewinnt. Leer = nur das To-do zeigen.
     */
    kandidaten: SprungKandidat[];
    /**
     * Der Faden neben einer Verlaufskarte, der für den Sprung aufgeklappt
     * werden muss (`zielSchluessel`, z. B. `mail:7`). Zugeklappt zeigt er nur
     * den jüngsten offenen Kommentar.
     */
    faden: string | null;
    /** Die Notiz gibt es nicht (mehr) — gelöscht oder verschoben. */
    fehlt: boolean;
};

/** Die gemeinte Notizzeile im geladenen Bestand. */
function findeNotiz(notizen: BlockNotiz[], notiz: string, todoId: number | null): BlockNotiz | null {
    if (notiz.includes(":")) return notizen.find((n) => notizSchluessel(n) === notiz) ?? null;
    // Nackte Id: zuerst die Quelle, die der Kontext nahelegt.
    const id = Number(notiz);
    const treffer = notizen.filter((n) => n.id === id);
    const bevorzugt = todoId != null ? "todo" : "vorgang";
    return treffer.find((n) => (n.source ?? "vorgang") === bevorzugt) ?? treffer[0] ?? null;
}

/**
 * Aus Sprung + geladenen Notizen das Ziel ableiten.
 *
 * Ein Kommentar steht an verschiedenen Orten, je nachdem, woran er hängt:
 *  · an einer Verlaufskarte (Mail, Anruf, …) in deren Kommentarspalte,
 *  · an einer Notiz hinter der Sprechblase ihres Abschnitts bzw. an der
 *    markierten Stelle (`data-anker`),
 *  · ohne erreichbares Ziel als eigener Abschnitt im Notizfeld.
 * Deshalb eine Kandidatenliste statt eines Selektors: der Beitrag selbst,
 * dann die Zeile als Abschnitt, die Textstelle, der Eltern-Abschnitt, die
 * Spalte, zuletzt die To-do-Karte.
 */
export function sprungZiel(sprung: Sprung, notizen: BlockNotiz[]): SprungZiel {
    const karte = (todoId: number | null): SprungKandidat[] =>
        todoId != null ? [{ attr: "data-todo-karte", wert: String(todoId) }] : [];
    if (!sprung.notiz) {
        return { todoId: sprung.todoId, kandidaten: karte(sprung.todoId), faden: null, fehlt: false };
    }
    const n = findeNotiz(notizen, sprung.notiz, sprung.todoId);
    if (!n) {
        return { todoId: sprung.todoId, kandidaten: karte(sprung.todoId), faden: null, fehlt: true };
    }
    const key = notizSchluessel(n);
    const todoId = n.todoId ?? sprung.todoId ?? null;
    const ziel = zielVon(n);
    const kandidaten: SprungKandidat[] = [];
    let faden: string | null = null;
    if (ziel) kandidaten.push({ attr: "data-kommentar", wert: key });
    kandidaten.push({ attr: "data-nk", wert: key });
    if (ziel?.art === "notiz") {
        if (n.anker) kandidaten.push({ attr: "data-anker", wert: n.anker });
        kandidaten.push({ attr: "data-nk", wert: `${ziel.quelle ?? "vorgang"}:${ziel.id}` });
    } else if (ziel) {
        faden = zielSchluessel(ziel);
        kandidaten.push({ attr: "data-faden", wert: faden });
    }
    kandidaten.push(...karte(todoId));
    return { todoId, kandidaten, faden, fehlt: false };
}

/**
 * Das erste sichtbare Element mit `attr === wert` — von Hand verglichen statt
 * per Attributselektor: `data-nk` trägt einen Doppelpunkt, und `CSS.escape`
 * gibt es nicht überall (jsdom). Am `data-nk` zählen nur die Abschnitte des
 * Feldes (`.nf-feld > *`), nicht die Randmarken, die denselben Schlüssel tragen.
 */
export function findeElement(wurzel: ParentNode, k: SprungKandidat): HTMLElement | null {
    for (const el of Array.from(wurzel.querySelectorAll<HTMLElement>(`[${k.attr}]`))) {
        if (el.getAttribute(k.attr) !== k.wert) continue;
        if (k.attr === "data-nk" && !el.parentElement?.classList.contains("nf-feld")) continue;
        return el;
    }
    return null;
}

/** Der erste Kandidat, der im DOM steht — mit seinem Rang (0 = der beste). */
export function besterTreffer(
    wurzel: ParentNode,
    kandidaten: SprungKandidat[],
): { el: HTMLElement; rang: number } | null {
    for (let i = 0; i < kandidaten.length; i++) {
        const el = findeElement(wurzel, kandidaten[i]!);
        if (el) return { el, rang: i };
    }
    return null;
}

/**
 * Die Markierung — eine CSS-Animation, die von selbst ausblendet. Der Name
 * kommt aus `notizFeldDom`, das sie beim Speichern wieder herausnimmt.
 */
export const MARKE_KLASSE = SPRUNG_MARKE_KLASSE;
/** So lange steht die Klasse; die Animation (vorgangV4.css) ist gleich lang. */
export const MARKE_MS = 1600;

/**
 * `display: contents` hat keinen Kasten — weder Scrollziel noch Markierung
 * wären zu sehen. Dann das erste Kind mit Kasten nehmen bzw. die Hülle.
 */
export function mitKasten(el: HTMLElement): HTMLElement {
    if (typeof window === "undefined" || window.getComputedStyle(el).display !== "contents") return el;
    return el.parentElement ?? el;
}

/** Hinscrollen und kurz golden hervorheben. Liefert eine Aufräumfunktion. */
export function springeZu(el: HTMLElement): () => void {
    const ziel = mitKasten(el);
    try {
        ziel.scrollIntoView({ block: "center", behavior: "smooth" });
    } catch {
        ziel.scrollIntoView?.();
    }
    ziel.classList.remove(MARKE_KLASSE);
    // Ein erneuter Sprung zur selben Stelle soll die Animation neu starten.
    void ziel.offsetWidth;
    ziel.classList.add(MARKE_KLASSE);
    const t = window.setTimeout(() => ziel.classList.remove(MARKE_KLASSE), MARKE_MS);
    return () => {
        window.clearTimeout(t);
        ziel.classList.remove(MARKE_KLASSE);
    };
}

/**
 * Ein Faden neben einer Verlaufskarte soll aufgehen — die Liste
 * (`EreignisListeV4`) hört darauf. Ein Ereignis statt einer Prop, weil die
 * Liste tief im Baum sitzt und je To-do einmal vorkommt; gemerkt wird dabei
 * nichts (`kmvs.vg4.kommentarAuf` bleibt unberührt).
 */
export const FADEN_AUF_EREIGNIS = "kmvs:vg4-faden-auf";

export function oeffneFaden(schluessel: string): void {
    window.dispatchEvent(new CustomEvent<string>(FADEN_AUF_EREIGNIS, { detail: schluessel }));
}

